import { ArrowLeftRight, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { displayName } from '../../model/people';
import { removeRelationship, upsertRelationship, wouldCreateCycle } from '../../model/tree';
import type { Relationship } from '../../model/types';
import { PARENT_TYPE_LABELS, PARTNER_STATUS_LABELS, PARTNER_TYPE_LABELS, SIBLING_TYPE_LABELS, SOCIAL_TYPE_LABELS } from '../labels';
import { closeDialog, commit, getState, toast, undo, useApp } from '../store';
import { Field, Modal, Select } from './common';
import { DateInput } from './DateInput';
import { PlaceInput } from './PlaceInput';

export function RelationshipEditor({ relId }: { relId: string }) {
  const tree = useApp((s) => s.tree)!;
  const original = tree.relationships[relId];
  const [r, setR] = useState<Relationship>(() => structuredClone(original));
  if (!original) return null;
  const na = displayName(tree.people[r.a]);
  const nb = displayName(tree.people[r.b]);
  const set = (patch: Partial<Relationship>) => setR((x) => ({ ...x, ...patch }) as Relationship);

  const title =
    r.kind === 'parent' ? `${na} → parent of ${nb}` : r.kind === 'social' && r.type === 'mentor' ? `${na} mentors ${nb}` : `${na} & ${nb}`;

  const save = () => {
    if (r.kind === 'parent' && (r.a !== original.a || r.b !== original.b)) {
      const without = removeRelationship(getState().tree!, relId);
      if (wouldCreateCycle(without, r.a, r.b)) return toast('That would make someone their own ancestor.', 'error');
    }
    commit(upsertRelationship(getState().tree!, r));
    closeDialog();
  };
  const del = () => {
    commit(removeRelationship(getState().tree!, relId));
    closeDialog();
    toast('Relationship removed.', 'info', { label: 'Undo', run: undo });
  };
  const canSwap = r.kind === 'parent' || (r.kind === 'social' && r.type === 'mentor');

  return (
    <Modal
      title={title}
      footer={
        <>
          <button className="btn danger left" onClick={del}>
            <Trash2 size={15} /> Remove relationship
          </button>
          <button className="btn" onClick={closeDialog}>
            Cancel
          </button>
          <button className="btn primary" onClick={save}>
            Save
          </button>
        </>
      }
    >
      <div className="stack">
        {canSwap && (
          <button className="btn small" style={{ alignSelf: 'flex-start' }} onClick={() => set({ a: r.b, b: r.a })}>
            <ArrowLeftRight size={14} /> Swap direction
          </button>
        )}
        {r.kind === 'parent' && (
          <Field label="Type of parent">
            <Select value={r.type} onChange={(type) => set({ type })} options={PARENT_TYPE_LABELS} />
          </Field>
        )}
        {r.kind === 'partner' && (
          <div className="grid-2">
            <Field label="Relationship">
              <Select value={r.type} onChange={(type) => set({ type })} options={PARTNER_TYPE_LABELS} />
            </Field>
            <Field label="Status">
              <Select value={r.status} onChange={(status) => set({ status })} options={PARTNER_STATUS_LABELS} />
            </Field>
          </div>
        )}
        {r.kind === 'sibling' && (
          <Field label="Type of sibling">
            <Select value={r.type} onChange={(type) => set({ type })} options={SIBLING_TYPE_LABELS} />
          </Field>
        )}
        {r.kind === 'social' && (
          <Field label="Connection">
            <Select value={r.type} onChange={(type) => set({ type })} options={SOCIAL_TYPE_LABELS} />
          </Field>
        )}
        {r.kind !== 'parent' && (
          <Field label="Custom label (optional)" hint="Shown instead of the default wording, e.g., “drag mother” or “wife.”">
            <input className="input" value={r.label ?? ''} onChange={(e) => set({ label: e.target.value || undefined })} />
          </Field>
        )}
        <div className="grid-2">
          <Field label={r.kind === 'partner' ? 'Started' : r.kind === 'parent' ? 'From (e.g., adoption date)' : 'Since'}>
            <DateInput value={r.start} onChange={(start) => set({ start })} />
          </Field>
          <Field label="Ended">
            <DateInput value={r.end} onChange={(end) => set({ end })} />
          </Field>
        </div>
        {r.kind === 'partner' && (
          <Field label="Place">
            <PlaceInput className="input" value={r.place ?? ''} placeholder="City, County, State, Country" onChange={(place) => set({ place: place || undefined })} />
          </Field>
        )}
        <Field label="Notes">
          <textarea className="textarea" value={r.notes ?? ''} onChange={(e) => set({ notes: e.target.value || undefined })} />
        </Field>
      </div>
    </Modal>
  );
}
